import React from 'react'
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import _ from 'lodash'
import moment from 'moment'
import regression from 'regression'

import { openFileToLine } from './Editor'
import { IChartPoint } from './types'

export interface IChartProps {
  data: IChartPoint[]
  keys: string[]
}

// https://www.paulirish.com/2009/random-hex-color-code-snippets/
// const randomColor = (): string => '#' + Math.floor(Math.random() * 16777215).toString(16)

// stolen from kepler
export const warm = (elements: number) => {

  const colors = [
    ['#e9668c'],
    ['#ee8695', '#a73b8f'],
    ['#ee8695', '#a73b8f', '#501b73'],
    ['#f19a9b', '#d54d88', '#7b2a95', '#461765'],
    ['#f4aea3', '#e8638b', '#a73b8f', '#61208d', '#3c1357'],
    ['#f6b5a4', '#eb7590', '#c8488a', '#872e93', '#581d7f', '#3a1353'],
    ['#f7bba6', '#ed8495', '#e05286', '#a73b8f', '#6f2597', '#511b75', '#37114e'],
    ['#f8c1a8', '#ef9198', '#e8608a', '#c0458a', '#8f3192', '#63218f', '#4b186c', '#33104a'],
    ['#f8c7aa', '#f19b9c', '#ea708e', '#d54d88', '#a73b8f', '#7a2995', '#5b1f84', '#451764', '#300f45'],
    ['#f9cdac', '#f2a49f', '#ec7c92', '#e65586', '#bc438b', '#933291', '#692398', '#551c7b', '#41155e', '#2d0f41']
  ]

  return colors[elements - 1]

}

interface IChartState {
  domain: {
    endIndex: number | undefined
    startIndex: number | undefined
  }
  label: string
}

class Chart extends React.Component<IChartProps, IChartState> {


  constructor(props: IChartProps) {
    super(props)
    this.state = {
      domain: {
        endIndex: undefined,
        startIndex: undefined,
      },
      label: '',
    }
    this.handleBrush = this.handleBrush.bind(this)
  }

  public componentDidMount() {
    if (this.props.data && this.props.data.length > 0) {
      this.computeTrend(0, this.props.data.length - 1)
    }
  }

  public computeTrend(startIndex: number, endIndex: number): void {

    if (this.props.data && this.props.data.length > 0 && this.props.data[0].chartType !== 'MEMORY') {
      // only do this for memory charts, this should really be injected as a render prop
      return
    }

    const dataLabel = 'com.mersive.solstice.server'

    const slicedData = this.props.data.slice(startIndex, endIndex)
    const mappedData: number[][] = slicedData.map(
      (point: IChartPoint): [number, number] => ([point.timestamp, point[dataLabel] as number])
      )
    const trendline = regression.linear(mappedData, { order: 2, precision: 5 })
    const gradient: number = trendline.equation[0] // the "m" in y = mx + b
    // const yIntercept: number = trendline.equation[1] // the "b" in y = mx + b
    const firstDataPoint: number[] = mappedData[0]
    const lastDataPoint: number[] = mappedData[slicedData.length - 1]
    const firstTime: number = firstDataPoint[0]
    const lastTime = lastDataPoint[0]
    const durationMs = lastTime - firstTime
    const durationHours = moment.duration(durationMs).asHours()
    const leakedMegabytes = durationMs * gradient / 1000

    const firstDate = moment(firstTime).format('MMM do HH:mm:ss')
    const lastDate = moment(lastTime).format('MMM do HH:mm:ss')

    const label = ` ${dataLabel} leaking ${(leakedMegabytes / durationHours).toPrecision(2)}MB/hour from ${firstDate} to ${lastDate}`
    this.setState(() => ({ ...this.state, label }))
  }

  public handleBrush = ({ startIndex, endIndex }: { startIndex: number, endIndex: number }) => {
    this.computeTrend(startIndex, endIndex)
  }

  public handleClick = (data: any): void => {
    const { fileId, lineNumber } = data.payload
    openFileToLine({
      fileId,
      lineNumber
    })
  }

  // public serializeCsv = (data: IChartPoint[]): BlobPart[] => {
  //   // For excel, yyyy-MM-dd HH:mm:ss
  //   let result = `time,${this.props.keys.reduce((acc: string, cur: string) => acc += `${cur},`, '')}\n`
  //   data.forEach((point: IChartPoint) => {
  //     result += `${point.timestamp},${this.props.keys.reduce((acc: string, cur: string) => acc += `${point[cur]},`, '')}\n`
  //   })
  //   return [result]
  // }

  // public handleDownload = () => {
  //   const data = new Blob(this.serializeCsv(this.props.data), { type: 'text/csv' })
  //   // create a temporary link element
  //   const tempLink = window.document.createElement('a')
  //   tempLink.href = window.URL.createObjectURL(data)
  //   tempLink.download = 'data.csv'
  //   document.body.appendChild(tempLink)
  //   // simulate a click to invoke the download
  //   tempLink.click()
  //   // remove the temporary element
  //   document.body.removeChild(tempLink)
  // }

  public render() {

    const { data, keys } = this.props

    const margin = {
      bottom: 50,
      left: 50,
      right: 50,
      top: 20,
    }

    const colors: string[] = warm(keys.length)

    return (
      <>
      {this.props.data && this.props.data.length > 0 &&
        <>
          {this.state.label.length > 0 && <p style={{ textAlign: 'center' }}>{this.state.label}</p>}
          <ResponsiveContainer>
            <LineChart data={data} margin={margin}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis domain={['auto', 'auto']}/>
              <Tooltip />
              {
                keys.map((key: string, index: number) => {
                  return (
                    <Line
                      activeDot={{onClick: this.handleClick}}
                      dataKey={key}
                      key={key}
                      stroke={colors[index]}
                      type={'monotone'}
                    />
                  )
                })
              }
              <Legend />
              <Brush onChange={this.handleBrush}/>
            </LineChart>
          </ResponsiveContainer>
        </>
      }
      </>
    )

  }

}

export default Chart



// WEBPACK FOOTER //
// ./src/Chart.tsx